import { InputTextarea } from "primereact/inputtextarea";
import React, { useEffect } from "react";
import useMessages from "../../../Pages/Process/actions/useMessages";
import PropTypes from "prop-types";
import CustomeButton from "../../Button";
import Attachments from "./Attachments";
import "./conversations.scss";
import ConversationPopup from "./ConversationPopup";
import ErrorMessage from "./ErrorMessage";
import { useSelector } from "react-redux";
import FileUploadModal from "./FileUploadModal";
import { FEATURE_FLAGS, useFeatureFlag } from "~/config/ff";
import { REQUEST_TYPES } from "~/Pages/Requests/useRequest";
import AttachmentConfirmationModal from "~/components/AttachmentConfirmationModal";
import { Carousel } from "primereact/carousel";

const MessageInput = ({
  fetchMessages,
  entitydata,
  relatedType,
  serviceData = {},
  requestdata = {},
  requestType = "",
  processdata = {},
}) => {
  const {
    isPublic,
    isMessageLoading,
    fileSupported,
    config,
    getService,
    messageText,
    setMessageText,
    charCounter,
    setCharCounter,
    showConfirmDialog,
    setShowConfirmDialog,
    isDataValid,
    setIsDataValid,
    isMessageSent,
    setIsMessageSent,
    showUploadModal,
    setShowUploadModal,
    files,
    setFiles,
    sendMessage,
    inputTextAreaRef,
    toggleNotification,
    setToggleNotification,
    send,
    showAttachmentConfirmation,
    setShowAttachmentConfirmation,
  } = useMessages({ fetchMessages, relatedType, requestType, serviceData });
  const { role } = useSelector((state) => state.login);

  const { value: isDocumentStorageFeatureEnabled } = useFeatureFlag(
    FEATURE_FLAGS.DOCUMENT_STORAGE,
  );

  const messageTextHandler = (event) => {
    const el = event.target;
    el.style.height = "44px";
    if (event.target.value) {
      el.style.height = Math.min(el.scrollHeight, 70) + "px";
    }
    setMessageText(event.target.value);
    setCharCounter(event.target.value.length);
  };

  const onFileRemove = (fileToRemove) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file !== fileToRemove));
  };
  const replaceFile = (fileInput, fileToReplace) => {
    let newFile;
    const selectedFile = fileInput.files[0];

    if (!fileSupported.includes(selectedFile.name.split(".").pop())) {
      newFile = { file: selectedFile, error: { state: true, visible: true } };
    } else {
      if (getFileSizeMB(selectedFile) > 20) {
        newFile = { file: selectedFile, error: { state: true, visible: true } };
      } else {
        newFile = {
          file: selectedFile,
          error: { state: false, visible: false },
        };
      }
    }

    setFiles((prevFiles) =>
      prevFiles.map((existingFileObj) =>
        existingFileObj.file === fileToReplace ? newFile : existingFileObj,
      ),
    );
  };

  const onUpdateVisibility = (fileToRemove) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file === fileToRemove
          ? { ...file, error: { state: true, visible: false } }
          : file,
      ),
    );
  };

  const uploadFileHandler = (file) => {
    setFiles((prevFiles) => [...prevFiles, ...file]);
  };

  const disableHandler = () => {
    if ((messageText || files.length > 0) && charCounter <= 4000) return false;
    else return true;
  };

  const handleConfirm = async () => {
    await sendMessage();
    setShowConfirmDialog(false);
  };
  const getFileSizeMB = (file) => (file.size / (1024 * 1024)).toFixed(2);
  useEffect(() => {
    let hasError = false;
    for (const element of files) {
      if (element.error.state) {
        hasError = true;
        break;
      }
    }
    setIsDataValid(!hasError);
  }, [files]);

  return (
    <div className="message-input-container">
      <InputTextarea
        ref={inputTextAreaRef}
        value={messageText}
        onChange={messageTextHandler}
        placeholder="Add your message here..."
        autoResize={false}
        className={`custom-textarea ${charCounter > 4000 && "error-input"}`}
      />
      {files.length > 0 && (
        <div className="attachment-layout">
          <h4>Attachments</h4>
          <div
            className={`attachment-carousel ${files.length <= 2 ? "no-padding" : ""}`}
          >
            <Carousel
              value={files}
              itemTemplate={(file, index) => (
                <Attachments
                  file={file.file}
                  key={index}
                  fileHandler={() => onFileRemove(file)}
                  icon="pi-times"
                  progressBar={isMessageLoading}
                  error={file.error}
                  handleErrorVisibility={() => onUpdateVisibility(file)}
                  getFileSizeMB={getFileSizeMB}
                  handleReplace={(file, fileToReplace) =>
                    replaceFile(file, fileToReplace)
                  }
                />
              )}
              numVisible={2}
              numScroll={2}
              showIndicators={false}
              showNavigators={files.length > 2}
              className="p-carousel-attachments"
            />
          </div>
        </div>
      )}

      {!isMessageSent && (
        <ErrorMessage
          summary={"File Upload Failed."}
          detail="Something went wrong. Please try uploading again."
          handleClose={() => {
            setIsMessageSent(true);
          }}
          otherAction={"Try Again"}
          handleOtherAction={send}
        />
      )}
      {charCounter > 4000 && (
        <div className="error-message-container">
          <i className=" pi pi-exclamation-circle" />
          <p className="error-message">
            Your text exceeds the maximum character limit
          </p>
        </div>
      )}
      <div className="actions-container">
        <p className="message-limit-text">
          {charCounter}/4000 characters allowed
        </p>
        <div className="action-btn-container">
          {isDocumentStorageFeatureEnabled &&
            requestType !== REQUEST_TYPES.ESCALATION && (
              <CustomeButton
                label="Add Attachment"
                variant="outlined"
                className="attachment-button  p-button-outlined"
                icon="pi pi-paperclip"
                iconPos="right"
                onClick={() => setShowUploadModal(true)}
              />
            )}
          <CustomeButton
            label="Send"
            variant="contained"
            className="send-button"
            icon="pi pi-send"
            iconPos="right"
            disabled={disableHandler() || !isDataValid}
            onClick={() => {
              if (files.length > 0) {
                setShowAttachmentConfirmation(true);
              } else {
                send(entitydata, requestdata);
              }
            }}
            loading={
              isPublic ? (config ? isMessageLoading : false) : isMessageLoading
            }
          />
        </div>
      </div>
      <AttachmentConfirmationModal
        showModal={showAttachmentConfirmation}
        handleCloseModal={() => setShowAttachmentConfirmation(false)}
        handleConfirm={() => send(entitydata, requestdata)}
      />
      <ConversationPopup
        visible={showConfirmDialog}
        onConfirm={handleConfirm}
        isMessageLoading={isMessageLoading}
        toggleNotification={toggleNotification}
        setToggleNotification={setToggleNotification}
        setShowConfirmDialog={setShowConfirmDialog}
        role={role}
      />
      <FileUploadModal
        entitydata={entitydata}
        processdata={processdata}
        showModal={showUploadModal}
        fileSupported={fileSupported}
        hideUploadModal={() => setShowUploadModal(false)}
        setUploadedFile={uploadFileHandler}
        getService={getService}
        requestdata={requestdata}
        relatedType={relatedType}
        uploadedFile={files}
      />
    </div>
  );
};

MessageInput.propTypes = {
  fetchMessages: PropTypes.func,
  requestType: PropTypes.string,
};

export default MessageInput;
